/**
 * Unified CMS Domain Types
 *
 * Brand-agnostic interfaces that both Gallet and Breitling
 * content models will be adapted to. These types represent
 * the application's domain model, not any specific CMS schema.
 *
 * Philosophy:
 * - Define what the application NEEDS, not what the CMS PROVIDES
 * - Keep types simple and focused on UI requirements
 * - Use optional fields for brand-specific features
 * - Document which brands support which fields
 */

// ============================================
// Base Types
// ============================================

/** @TODO: do we really need the | string */
export type UrlType =
  | 'none'
  | 'button'
  | 'buttonDefault'
  | 'buttonUnderline'
  | 'buttonFilledBlack'
  | 'buttonWhite'
  | 'buttonOutlinePrimary'
  | 'buttonOutlineBlack'
  | 'buttonOutlineBlue'
  | 'buttonOutlineWhite'
  | 'linkWhite'
  | 'linkBlack'
  | 'linkDefault'
  | 'primaryBtn'
  | 'secondaryBtn'
  | 'arrowOutlineWhite'
  | string
  | null;

/** @TODO: do we really need the | string */
export type UrlEventType =
  | 'Default'
  | 'OpenVideoPopup'
  | 'OpenSelectorPopup'
  | 'OpenPopup'
  | string;

/** @TODO: do we really need the | string */
export type UrlTrigger =
  | 'openStoreLocator'
  | 'openContactUs'
  | 'openNotifyMe'
  | 'scrollToElement'
  | 'Newsletter'
  | string
  | null;

/**
 * Unique identifier for CMS entries
 */
export interface CMSSys {
  id: string;
  publishedAt?: string | null;
}

/**
 * Base interface for all CMS content sections
 */
export interface CMSSection {
  __typename: string;
  sys: CMSSys;
  internalName?: string | null;
  /** Scroll-triggered entry animation. Stub until Contentful field is created. */
  entryEffect?: boolean | null;
  /** Editor-defined HTML anchor ID from Contentful (distinct from sys.id) */
  anchorId?: string | null;
  /**
   * Country codes (lowercase, ISO 3166-1 alpha-2) where this section is enabled.
   * `null` or empty array means the section is enabled in every country.
   */
  localizationConfiguration?: string[] | null;
}

/** CMS component __typename constants for type-safe routing in cmsRenderer. */
export const CMSComponentType = {
  DoubleBanner: 'DoubleBanner',
  CategoryBannerGrid: 'CategoryBannerGrid',
  TrendingProducts: 'TrendingProducts',
  StoriesBanner: 'StoriesBanner',
  StoriesList: 'StoriesList',
  Timeline: 'Timeline',
  EBoutiqueServices: 'EBoutiqueServices',
  Popup: 'Popup',
} as const;

// ============================================
// Media Types
// ============================================

/**
 * Image or video asset
 */
export interface CMSMedia {
  url: string;
  title?: string | null;
  alt?: string | null;
  description?: string | null;
  width?: number | null;
  height?: number | null;
  contentType?: string | null;
  /** Focal point for image cropping (0-1 range) */
  focalPoint?: { x: number; y: number } | null;
}

/**
 * Image with optional mobile variant
 */
export interface CMSResponsiveMedia {
  desktop?: CMSMedia | null;
  mobile?: CMSMedia | null;
  tablet?: CMSMedia | null;
}

/**
 * One Magazine article as the UG listing blocks consume it, adapted from a `page`
 * entry linked by a magazine card's `articles` field.
 */
export interface CMSMagazineArticle {
  id: string;
  /** Without the `magazine/` prefix; the route adds it. */
  slug: string;
  title: string | null;
  excerpt: string | null;
  /** Small uppercase label above the title, e.g. "SPOTLIGHT". */
  overline: string | null;
  media: CMSResponsiveMedia | null;
  /** Falls back to the title at render time. */
  mediaAlt: string | null;
  /** Drives the feed order. Articles without one sort last. */
  publishDate: string | null;
}

/**
 * Video with optional poster image
 */
export interface CMSVideo {
  video: CMSMedia;
  poster?: CMSMedia | null;
  autoplay?: boolean | null;
  loop?: boolean | null;
  muted?: boolean | null;
}

/**
 * Standalone image section (used inside Grid)
 * For ImageWithFocalPoint Contentful type when used as content item
 */
export interface CMSImage extends CMSSection {
  __typename: 'ImageWithFocalPoint';
  image?: CMSMedia | null;
  imageMobile?: CMSMedia | null;
  imageOnHover?: CMSMedia | null;
  video?: CMSMedia | null;
  videoMobile?: CMSMedia | null;
  alt?: string | null;
  url?: string | null;
}

// ============================================
// Text & Rich Content
// ============================================

/**
 * Rich text content (from Contentful Rich Text)
 */
/**
 * Editorial copy for a UG customer-interest form (BEC-24125).
 *
 * Every field is nullable on purpose: the components resolve
 * authored-then-translated, so a null here means "use the message catalogue",
 * never "render nothing".
 *
 * No `variant` field: the query filters on it server-side and each drawer is
 * constructed for one variant, so carrying it here only invited the adapter to
 * invent a default that nothing read.
 */
export interface CMSFormCopy {
  title: string | null;
  intro: string | null;
  submitLabel: string | null;
  consentMarketing: CMSRichText | null;
  /**
   * The privacy line for the UK, Switzerland, China and the EU-27, which live
   * shows instead of the one authored inside `consentMarketing`. Null falls back
   * to the translated string, so the legal copy is never absent.
   */
  consentPrivacyNoticeLegacy: CMSRichText | null;
  consentConditionsOfSale: CMSRichText | null;
  consentDataOutOfChina: CMSRichText | null;
  successTitle: string | null;
  successBody: string | null;
  errorGeneric: string | null;
}

export interface CMSRichText {
  json: unknown; // Contentful Rich Text JSON structure
  links?: {
    assets?: CMSMedia[];
    entries?: CMSSection[];
  };
}

/**
 * Event entry used as CTA trigger payload (Contentful "event" content type).
 */
export interface CMSEvent {
  __typename?: 'Event';
  sys: CMSSys;
  type?: string | null;
  sfId?: string | null;
  campaignName?: string | null;
  eventId?: string | null;
  title?: string | null;
  description?: CMSRichText | null;
}

/**
 * Key-value tag from Contentful tagsCollection
 */
export interface CMSTag {
  key: string;
  value: string;
}

/**
 * Call-to-action link
 */
export interface CMSCallToAction {
  text: string;
  url: string;
  target?: '_blank' | '_self' | null;
  variant?: UrlType;
  icon?: string | null;
  eventType?: UrlEventType;
  eventTrigger?: CMSEvent | null;
}

// ============================================
// Product Page Sections
// ============================================

/**
 * Product details placeholder
 * Contains hero, gallery, and product info
 */
/**
 * UG PDP display modes. Gallet and Breitling leave `displayMode`
 * unset — their spaces have no such field.
 */
export type CMSProductDetailsDisplayMode = 'productVariant' | 'coutureSelector';

export interface CMSPlaceholderProductDetails extends CMSSection {
  __typename: 'PlaceholderProductDetails';
  watchCollection?: string | null;
  perks?: CMSServicePerk[] | null;

  // UG-only. All optional and absent for Gallet and Breitling, whose
  // renderer path never reads them. UG's Contentful space has one `placeholder`
  // type; the UG adapter splits it into this and `CMSPlaceholder` on `type`.
  displayMode?: CMSProductDetailsDisplayMode | null;
  showPrice?: boolean | null;
  showModelSelector?: boolean | null;
  /**
   * CTA copy. The buttons themselves are fixed by product class — pre-order plus
   * request-information for collection, availability-request for couture — so
   * these carry labels only, never presence.
   */
  ctaPreOrderLabel?: string | null;
  ctaRequestInformationLabel?: string | null;
  ctaRequestAvailabilityLabel?: string | null;
  ctaSubtext?: string | null;
  priceInfoText?: string | null;
  aiDisclaimer?: string | null;
}

/**
 * Warranty text content structure
 * Matches old solution's ContentfulPlaceholderFragment['warrantyText']
 */
export interface CMSWarrantyTextContent {
  placeholderWarrantyHeading?: { text: string | null } | null;
  placeholderWarrantyText?: { json: unknown } | null;
  placeholderWarrantyText2?: { json: unknown } | null;
  url?: string | null;
  urlText?: string | null;
  subHeading?: string | null;
  type?: string | null;
}

/**
 * "Taking Care of Your Watch" content structure
 * Matches old solution's ContentfulPlaceholderFragment['warrantyTakingCareOfYourWatch']
 */
export interface CMSWarrantyTakingCareContent {
  media?: CMSMedia | null;
  placeholderWarrantyHeading?: { text: string | null } | null;
  placeholderWarrantyText?: { json: unknown } | null;
  placeholderWarrantyText2?: { json: unknown } | null;
  url?: string | null;
  urlText?: string | null;
  type?: string | null;
}

/**
 * Generic placeholder for dynamic content
 * Used for: key features, technical data, trending products, warranty, etc.
 */
/**
 * `Placeholder.type` marking the UG couture landing page's all-collection
 * carousel. Lives here rather than beside its renderer so the CMS-side detector
 * (`lib/cms/ugCouturePage.ts`) and the render-side dispatch table
 * (`components/cms/CmsRenderer`) cannot drift apart. Compared lowercased and
 * trimmed at both ends.
 */
export const COLLECTION_PRODUCTS_PLACEHOLDER = 'collection products';

/**
 * `placeholder.type` marking where the Stay Informed newsletter form renders
 * (BEC-24123). Added to the UG space's `type` whitelist on 2026-09-02.
 */
export const NEWSLETTER_FORM_PLACEHOLDER = 'newsletter form';

/**
 * Key the newsletter's authored copy travels under inside `extraData`.
 *
 * Declared here rather than beside its fetcher: `CmsRenderer` is a client
 * component and needs this constant, while `lib/cms/ugNewsletterPage.ts` pulls
 * in the server-side Contentful client. Importing the key from there would drag
 * that into the browser bundle.
 */
export const UG_NEWSLETTER_EXTRA_DATA_KEY = 'ugNewsletterFormCopy';

export interface CMSPlaceholder extends CMSSection {
  __typename: 'Placeholder';
  /**
   * UG-only. The Saleor collection slug this section reads its products from.
   * Mirrors `DetailPage.watchCollection`, the existing UG CMS↔commerce binding.
   * Authored rather than compiled in, so a second collection landing page needs
   * no deploy (BEC-24834).
   */
  watchCollection?: string | null;
  type?: string | null; // 'key features', 'technical data', 'trending products', 'warranty', etc.

  /**
   * Section heading authored on the placeholder itself, so it is shared rather
   * than warranty-specific: `type='warranty'` renders it as the block's title,
   * and UG's `newsletter form` placeholder (BEC-24123) uses it as an optional
   * heading above the form. Adding a third reader means checking both.
   */
  heading?: string | null;

  // Warranty-specific fields (populated when type='warranty')
  subtext?: { json: unknown } | null;
  videoUrl?: string | null;
  warrantyText?: CMSWarrantyTextContent | null;
  warrantyTakingCareOfYourWatch?: CMSWarrantyTakingCareContent | null;

  // 'product listing' specific: editorial cards that occupy specific cells of
  // the desktop product grid (4 columns). Each override pins a Card to a
  // (row, columnIndex) and a span (1 or 3). Adapted from PlpTileOverride
  // entries in Contentful.
  tileOverrides?: CMSPlpTileOverride[] | null;

  // 'product listing' specific: when true, the grid renders only the first row
  // of products initially with a "See all" expander. CMS-driven via the
  // placeholder.initiallyCollapsed Boolean.
  initiallyCollapsed?: boolean | null;
}

/**
 * Boutique detail info block (address, contact, hours, appointment CTA).
 * Contentful PlaceholderBoutiquesInfo or Placeholder type "boutique info detail".
 */
export interface CMSPlaceholderBoutiquesInfo extends CMSSection {
  __typename: 'PlaceholderBoutiquesInfo';
  type?: string | null;
}

/**
 * Boutique detail about block (store description + gallery image).
 * Contentful PlaceholderBoutiquesAbout or Placeholder type "boutique detail about".
 */
export interface CMSPlaceholderBoutiquesAbout extends CMSSection {
  __typename: 'PlaceholderBoutiquesAbout';
  type?: string | null;
}

/**
 * Editorial card injection into the PLP product grid.
 *
 * - `row`: 1-indexed grid row (top is 1).
 * - `columnIndex`: 1-indexed start column in the 4-col desktop grid.
 * - `span`: how many columns the card spans (1 or 3). When span=3 the card
 *           must start at columnIndex 1 or 2; the renderer skips overrides
 *           that would overflow.
 */
export interface CMSPlpTileOverride {
  __typename: 'PlpTileOverride';
  sys: CMSSys;
  internalName?: string | null;
  row: number;
  columnIndex: number;
  span: number;
  card: CMSCard;
}

/**
 * PDP model-story placeholder from Contentful.
 * Gates whether ProductStory renders and in which CMS order; Saleor fills title/content/image
 * via `extraData.productDetails.modelStory` when present (CMS media/bg/alignment win for layout).
 */
export interface CMSPlaceholderModelStory extends CMSSection {
  __typename: 'PlaceholderModelStory';
  /** Fallback title when Saleor modelStory.title is absent. */
  storyTitle?: string | null;
  /** Fallback body when Saleor modelStory.content is absent. */
  storyContent?: CMSRichText | string | null;
  /** Preferred story image; Saleor product media is used only when this is empty. */
  media?: CMSMedia | null;
  backgroundColor?: string | null;
  /** CMS media side — e.g. "left" / "right" (drives ProductStory `mediaReversed`). */
  mediaAlignment?: string | null;
}

/**
 * FindAStore card configuration (from Contentful FindAStore type)
 */
export interface CMSFindAStoreData {
  sys?: CMSSys;
  type?: string | null;
  url?: string | null;
  urlText?: string | null;
  backgroundColor?: string | null;
  cardImage?: CMSMedia | null;
  longImage?: CMSMedia | null;
  text?: {
    json: unknown; // Rich text JSON
  } | null;
}

/**
 * Placeholder for store/boutique listings
 * Displays boutique cards in a slider format
 */
/**
 * Service placeholder — routes to interactive service widgets (price search, etc.)
 */
export interface CMSPlaceholderService extends CMSSection {
  __typename: 'PlaceholderService';
  type?: string | null;
  placeholderHeading?: {
    text?: string | null;
    headingType?: string | null;
    headingSeoTag?: string | null;
    headingVariation?: string | null;
    mainColor?: string | null;
    highlightColor?: string | null;
    textAlignment?: string | null;
  } | null;
  subtext?: { json?: unknown } | null;
}

export interface CMSPlaceholderStores extends CMSSection {
  __typename: 'PlaceholderStores';
  type?: 'Default' | 'Search' | null;
  numberOfStores?: number | null;
  backgroundColor?: string | null;
  heading?: {
    text?: string | null;
    headingType?: string | null;
    headingSeoTag?: string | null;
    headingVariation?: string | null;
    mainColor?: string | null;
    highlightColor?: string | null;
    textAlignment?: string | null;
  } | null;
  findAStore?: CMSFindAStoreData | null;
  /** Curated list of boutique reference keys to pre-filter the displayed stores */
  boutiqueReferenceKeys?: string[] | null;
  /** Restrict the displayed stores to those that stock Breitling eyewear */
  eyewearAvailable?: boolean | null;
}

/**
 * Service perk (free shipping, warranty, etc.)
 */
export interface CMSServicePerk {
  sys?: CMSSys;
  icon?: CMSMedia | null;
  title?: string | null;
  description?: string | null;
  /** Perk type from Contentful (e.g., 'free shipping', 'free return') */
  type?: string | null;
  /** Rich text content for modal */
  modalText?: { json: unknown } | null;
  /** URL for "Find out more" link */
  url?: string | null;
  /**
   * Country codes (lowercase, ISO 3166-1 alpha-2) where this perk is enabled.
   * `null` or empty array means the perk is enabled in every country.
   */
  localizationConfiguration?: string[] | null;
}

/**
 * Product card for listings/carousels
 * Matches ProductCardData from @/components/brand for full Breitling card rendering
 */
export interface CMSProductCard {
  sys: CMSSys;
  name: string;
  subtitle?: string | null;
  sku?: string | null;
  price?: number | null;
  currency?: string | null;
  /** Pre-formatted price string (e.g., "$5,000") - populated by orchestrator */
  formattedPrice?: string | null;
  image?: CMSMedia | null;
  href?: string | null;
  /** Product label/badge (e.g., "NEW", "LIMITED") - populated by orchestrator */
  label?: string | null;
  /** Saleor variant ID - required for add to cart */
  variantId?: string | null;
  /** CTA type from Algolia (ORDER, PRE_ORDER, NOTIFY, DISCOVER, NO_CTA, etc.) */
  ctaType?: string | null;
  /** Case material (e.g., "Stainless steel & 18k red gold") */
  material?: string | null;
  /** Product type for styling variations */
  productType?: 'Watch' | 'Strap' | 'Eyewear' | null;
  /** Whether strap has integrated buckle (false = "Buckle not included" message) */
  integratedBuckle?: boolean | null;
}

/**
 * Trending products / Product slider section
 *
 * Products can be:
 * 1. Directly provided as CMSProductCard[] (populated by orchestrator)
 * 2. Referenced by SKU strings that need server-side fetching
 * 3. Auto-generated based on `sliderType` (e.g., "recently viewed", "you may also like")
 *
 * The orchestrator handles fetching product data for SKU-based sliders via BFF.
 */
export interface CMSTrendingProducts extends CMSSection {
  __typename: 'TrendingProducts' | 'SliderProductsAutomatic' | 'SliderProductsManual';
  heading?: {
    text?: string | null;
    headingType?: string | null;
    headingSeoTag?: string | null;
    headingVariation?: string | null;
    mainColor?: string | null;
    highlightColor?: string | null;
    textAlignment?: string | null;
  } | null;
  /** Populated product data (from CMS or fetched by orchestrator) */
  products: CMSProductCard[];
  /** SKU array from CMS - orchestrator fetches product data for these */
  productSkus?: string[] | null;
  /** Slider type for automatic product selection (e.g., "recently viewed", "you may also like") */
  sliderType?: string | null;
  /** Display configuration */
  showCta?: boolean | null;
  showPrice?: boolean | null;
  showName?: boolean | null;
  showLabel?: boolean | null;
  showWishlist?: boolean | null;
  slidesPerView?: number | null;
  slidesPerViewMobile?: number | null;
  backgroundColor?: string | null;
  productCardBackgroundColor?: string | null;
  backgroundImage?: CMSMedia | null;
  /** Bottom-of-carousel CTA: button text */
  ctaText?: string | null;
  /** Bottom-of-carousel CTA: visual style ('primary', 'secondary', 'link', 'none') */
  ctaStyle?: string | null;
  /** Bottom-of-carousel CTA: target URL */
  ctaUrl?: string | null;
  /** Bottom-of-carousel CTA: aria-label override */
  ctaAriaLabel?: string | null;
}

/**
 * Register watch CTA section
 */
export interface CMSRegisterWatch extends CMSSection {
  __typename: 'RegisterWatch' | 'Placeholder';
  heading?: string | null;
  description?: string | null;
  cta?: CMSCallToAction | null;
}

// ============================================
// Gallet-Specific Sections
// ============================================

/**
 * Stories banner (Gallet world clock + featured story)
 */
export interface CMSStoriesBanner extends CMSSection {
  __typename: 'StoriesBanner';
  // Content fields
  layout?: string | null;
  label?: string | null;
  heading?: string | null;
  bodyText?: string | null;
  ctaText?: string | null;
  ctaUrl?: string | null;
  // Visual configuration
  contentPanelBackground?: string | null;
  contentPanelBackgroundColor?: string | null;
  contentPanelBackgroundMedia?: CMSMedia | null;
  worldClockImage?: CMSMedia | null;
  ringPosition?: string | null;
  showOnlyTopCity?: boolean | null;
  autoslideDuration?: number | null;
  // Collections
  featuredStory?: CMSStorySlide | null;
  stories?: CMSStorySlide[] | null;
  cities?: CMSCityPair[] | null;
}

/**
 * Stories list (Gallet-specific grid of story cards)
 */
export interface CMSStoriesList extends CMSSection {
  __typename: 'StoriesList';
  title?: string | null;
  latest?: boolean | null;
  filters?: boolean | null;
  buttonLabel?: string | null;
  buttonUrl?: string | null;
}

/**
 * Timeline entry (Gallet-specific)
 */
export interface CMSTimelineEntry {
  __typename: 'TimelineEntry';
  sys: CMSSys;
  year: number;
  title?: string | null;
  backgroundImage?: CMSResponsiveMedia | null;
  cta?: CMSPopup | null;
}

/**
 * Timeline parent (Gallet-specific)
 * Contains an ordered collection of TimelineEntry children and a shared CTA label.
 */
export interface CMSTimeline extends CMSSection {
  __typename: 'Timeline';
  id?: string | null;
  ctaLabel?: string | null;
  entries: CMSTimelineEntry[];
  stickyScroll: boolean;
}

/**
 * Story slide
 */
export interface CMSStorySlide {
  sys: CMSSys;
  title?: string | null;
  description?: string | null;
  media?: CMSMedia | null;
  cta?: CMSCallToAction | null;
}

/**
 * City pair for world clock
 * Schema: city12 (city name for 12-hour side), city24 (city for 24-hour side), rotation
 */
export interface CMSCityPair {
  sys?: CMSSys;
  city12?: string | null;
  city24?: string | null;
  rotation?: number | null;
}

/**
 * Category banner grid (collection cards)
 */
export interface CMSCategoryBannerGrid extends CMSSection {
  __typename: 'CategoryBannerGrid';
  heading?: string | null;
  items: CMSCategoryBannerItem[];
  // Grid configuration
  gridColumns?: string | null;
  gridGap?: string | null;
  marginTop?: string | null;
  marginBottom?: string | null;
  componentPadding?: string | null;
  bannerAspectRatio?: string | null;
  transitionType?: string | null;
  backgroundImage?: CMSMedia | null;
}

/**
 * Category banner item
 */
export interface CMSCategoryBannerItem {
  sys: CMSSys;
  title?: string | null;
  subtitle?: string | null;
  media?: CMSMedia | null;
  productImage?: CMSMedia | null;
  cta?: CMSCallToAction | null;
  backgroundColor?: string | null;
  // Banner item configuration
  hoverEffect?: string | null;
  transitionType?: string | null;
  textAlignment?: string | null;
  contentPosition?: string | null;
  textColorPreset?: string | null;
  textColorCustom?: string | null;
  overlayOpacity?: number | null;
  parallaxOverlayColorPreset?: string | null;
  parallaxOverlayColorCustom?: string | null;
  ctaColor?: string | null;
}

/**
 * Double banner (2-panel promotional layout)
 */
export interface CMSDoubleBanner extends CMSSection {
  __typename: 'DoubleBanner';
  leftBanner?: CMSBanner | null;
  rightBanner?: CMSBanner | null;
}

/**
 * E-boutique services grid
 */
export interface CMSEBoutiqueServices extends CMSSection {
  __typename: 'EBoutiqueServices';
  heading?: string | null;
  services: CMSServicePerk[];
}

export interface CMSPopup extends CMSSection {
  __typename: 'Popup';
  animation?: 'fade' | 'slideRight' | 'slideDown' | null;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | null;
  backgroundColor?: string | null;
  content?: CMSContentItem[] | null;
}

export const POPUP_ANIMATION_MAP: Record<string, CMSPopup['animation']> = {
  fade: 'fade',
  'slide right': 'slideRight',
  'slide down': 'slideDown',
};

export const POPUP_MAX_WIDTH_MAP: Record<string, CMSPopup['maxWidth']> = {
  full: 'full',
  xl: 'xl',
  l: 'lg',
  m: 'md',
  s: 'sm',
  xs: 'xs',
};

// ============================================
// Listing Page Sections
// ============================================

/**
 * Hero for Product Listing Pages (PLP)
 * Controls heading, media, breadcrumbs toggle, and filter toggle
 */
export interface CMSHeroProductListing extends CMSSection {
  __typename: 'HeroProductListing';
  type?: string | null;
  heading?: string | null;
  headingNumberOfColoredWords?: number | null;
  headingSeoTag?: string | null;
  headingStyle?: string | null;
  subheader?: string | null;
  subheading?: string | null;
  subheaderIsCapitalized?: boolean | null;
  textBelowMedia?: boolean | null;
  textAlignment?: 'left' | 'center' | 'right' | null;
  textColor?: string | null;
  showFilters?: boolean | null;
  showBreadcrumbs?: boolean | null;
  backgroundColor?: string | null;
  backgroundColorOpacity?: number | null;
  media?: CMSMedia | null;
  collectionItems?: CMSCollectionItem[] | null;
}

// ============================================
// Breitling-Specific Sections
// ============================================

/**
 * Collection group (Breitling collection showcase)
 *
 * Displays products from a collection with optional banner and end card.
 * Products are populated by the orchestrator based on:
 * - collectionName: Algolia collection filter
 * - productSkus: Manual SKU list
 */
export interface CMSCollectionGroup extends CMSSection {
  __typename: 'CollectionGroup';
  heading?: string | null;
  /** Display type (e.g., "one_hero_product", "grid") */
  type?: string | null;
  /** Optional hero banner */
  banner?: CMSBanner | null;
  /** Collection name for automatic product fetch */
  collectionName?: string | null;
  /** Algolia facet filters (JSON string) */
  algoliaFacetFilters?: string | null;
  /** Maximum products to show */
  maxProducts?: number | null;
  /** Sort order */
  sortBy?: string | null;
  /** Manual product SKUs (alternative to collection) */
  productSkus?: string[] | null;
  /** Populated products (from orchestrator) */
  products: CMSProductCard[];
  /** End card configuration */
  endCard?: CMSCollectionEndCard | null;
  /**
   * Editorial cards that occupy specific cells of the CollectionGroup's
   * desktop 4-column product grid — same shape as Placeholder.tileOverrides.
   * Authored as plpTileOverride entries linked from CollectionGroup.
   */
  tileOverrides?: CMSPlpTileOverride[] | null;
  /**
   * Section background. If only `backgroundColor` is set the section paints a
   * solid colour. If `backgroundColorEnd` is also set, the section paints a
   * linear-gradient from `backgroundColor` to `backgroundColorEnd` along
   * `backgroundGradientDirection` (defaults to "to bottom"). All three are
   * authored on the CollectionGroup entry in Contentful.
   */
  backgroundColor?: string | null;
  backgroundColorEnd?: string | null;
  backgroundGradientDirection?: string | null;
  /**
   * Optional author-controlled DOM id used as the in-page anchor target by
   * `CollectionsRow` (scroll mode). Lowercase letters / digits / hyphens.
   * When unset, the component falls back to `cg-{sysId}`.
   */
  anchorSlug?: string | null;
  /** @deprecated Use products array instead */
  collections: CMSCollectionItem[];
}

/**
 * Collection end card (CTA at end of collection group)
 */
export interface CMSCollectionEndCard {
  sys: CMSSys;
  image?: CMSMedia | null;
  linkUrl?: string | null;
  linkText?: string | null;
  linkType?: string | null;
}

/**
 * Collection item
 * @deprecated Use CMSProductCard for products
 */
export interface CMSCollectionItem {
  sys: CMSSys;
  name?: string | null;
  slug?: string | null;
  image?: CMSMedia | null;
  description?: string | null;
}

/**
 * Services section (Breitling)
 */
export interface CMSServices extends CMSSection {
  __typename: 'Services';
  heading?: string | null;
  services: CMSService[];
}

/**
 * Individual service
 */
export interface CMSService {
  sys: CMSSys;
  title?: string | null;
  description?: string | null;
  icon?: CMSMedia | null;
  cta?: CMSCallToAction | null;
}

/**
 * Find a store section
 * Contains all fields from Contentful FindAStore type
 */
export interface CMSFindAStore extends CMSSection {
  __typename: 'FindAStore';
  heading?: string | null;
  description?: string | null;
  cta?: CMSCallToAction | null;
  // Additional fields from Contentful FindAStore
  cardImage?: CMSMedia | null;
  longImage?: CMSMedia | null;
  backgroundColor?: string | null;
  text?: { json: unknown } | null;
  url?: string | null;
  urlText?: string | null;
  // Nested data for PlaceholderStores component
  findAStoreData?: CMSFindAStoreData | null;
}

/**
 * Contact us section
 */
export type CMSContactHeading = {
  text?: string | null;
  headingType?: string | null;
  headingSeoTag?: string | null;
  headingVariation?: string | null;
  mainColor?: string | null;
  highlightColor?: string | null;
  textAlignment?: string | null;
};

export interface CMSContactUs extends CMSSection {
  __typename: 'ContactUs';
  heading?: string | CMSContactHeading | null;
  description?: string | null;
  phone?: string | null;
  email?: string | null;
  cta?: CMSCallToAction | null;
  /** Variant discriminator from Contentful (e.g. 'Default', 'AfterSales') */
  type?: string | null;
  /** Optional cover-style background image for the contact panel */
  backgroundMedia?: CMSMedia | null;
}

/**
 * FAQ section
 */
export interface CMSFaq extends CMSSection {
  __typename: 'FaqWrap';
  heading?: string | null;
  categories?: CMSFaqCategory[] | null;
  collapse?: boolean | null;
}

/**
 * FAQ categories section with sidebar navigation
 */
export interface CMSFaqCategories extends CMSSection {
  __typename: 'FaqCategories';
  categoryLabel?: string | null;
  faqWraps?: CMSFaq[] | null;
}

/**
 * FAQ category
 */
export interface CMSFaqCategory {
  sys: CMSSys;
  name?: string | null;
  questions: CMSFaqItem[];
}

/**
 * FAQ item
 */
export interface CMSFaqItem {
  sys?: CMSSys;
  question: string;
  answer: CMSRichText | string;
  localizationConfiguration?: string[] | null;
}

// ============================================
// Content Components (Shared)
// ============================================

/**
 * Heading component
 */
export interface CMSHeading extends CMSSection {
  __typename: 'Heading';
  text?: string | null;
  headingType?: string | null;
  headingSeoTag?: string | null;
  headingVariation?: string | null;
  mainColor?: string | null;
  highlightColor?: string | null;
  textAlignment?: string | null;
}

/**
 * Free text content (rich text block)
 */
export interface CMSFreeTextContent extends CMSSection {
  __typename: 'FreeTextContent';
  freeTextContent?: CMSRichText | unknown | null;
}

/**
 * Grid layout component
 */
export interface CMSGrid extends CMSSection {
  __typename: 'Grid';
  columns?: number | null;
  columnsMobile?: number | null;
  contentCollection?: { items?: CMSContentItem[] | null } | null;
  paddingTop?: string | null;
  paddingBottom?: string | null;
  backgroundColor?: string | null;
  /** Optional cover-style background asset (image or video) for the entire
   *  grid container. Renders above the background color and below the
   *  content. Single Contentful Asset — no mobile/hover variants. */
  backgroundMedia?: CMSMedia | null;
  visibleInDesktop?: boolean | null;
  visibleInMobile?: boolean | null;
  variant?: 'flushLeft' | null;
  /** Render as a horizontal slider (mobile scroll-snap) instead of a grid */
  slider?: boolean | null;
  /** Reverse first/last item order on mobile */
  reverseMobile?: boolean | null;
  /** Container max-width preset ('sm' | 'md' | 'lg' | 'xl' | 'full') */
  maxWidth?: string | null;
  /** Vertical alignment of grid items — raw Contentful value (e.g. 'Left', 'Center', 'Right') */
  align?: string | null;
  /** Row gap — raw Contentful value (e.g. 'Default', 'Small', 'Large', 'None', or numeric) */
  rowGap?: string | null;
  /** Column gap — raw Contentful value (e.g. 'Default', 'Small', 'Large', 'None', or numeric) */
  colGap?: string | null;
  /** Vertical alignment of grid items within their cells */
  verticalAlignment?: 'top' | 'center' | 'bottom' | string | null;
  /**
   * UG only: render this grid as a collection-capsule row (live
   * `o-collectionCapsule`) instead of the shared CSS-grid layout — a flex row
   * with its own 988px container, `CardCapsule` children and live's stagger.
   *
   * Deliberately a flag on `CMSGrid` rather than a separate `__typename`: five
   * places key off `__typename === 'Grid'` to recurse into `contentCollection`
   * (the live-preview card walkers and reconciliation, the popup-id collector,
   * and `TRIGGER_TYPENAMES` for the entry-effect variant). A new typename
   * silently bypassed all of them. It is a Grid; only its rendering differs.
   */
  capsuleRow?: boolean | null;
  /**
   * UG only: live `-staggered` on a capsule row — every even card drops on
   * desktop. Meaningless unless `capsuleRow` is set, and ignored at one column.
   */
  staggered?: boolean | null;
}

/**
 * Universal Card — new-generation card (BEC Universal components).
 * Layout options live directly on the card (paddings, alignments, media
 * heights) instead of a linked Layout entry.
 */
export interface CMSUniversalCard extends CMSSection {
  __typename: 'UniversalCard';
  /** Layout variant: Text | MediaBackground | MediaTop | MediaBottom | MediaLeft | MediaRight */
  type?: string | null;
  subtitleTop?: string | null;
  subtitleBottom?: string | null;
  /** Heading text from the linked Heading entry */
  heading?: string | null;
  /** SEO tag (h1..h6) from the linked Heading entry */
  headingTag?: string | null;
  /** Visual heading size from the linked Heading entry */
  headingType?: string | null;
  headingColor?: string | null;
  headingHighlightColor?: string | null;
  headingVariation?: string | null;
  headingTextAlignment?: string | null;
  richText?: CMSRichText | null;
  cta1?: CMSCallToAction | null;
  cta2?: CMSCallToAction | null;
  /** 'Inline' (side by side) or 'rows' (stacked) */
  ctaLayout?: string | null;
  image?: CMSMedia | null;
  mobileImage?: CMSMedia | null;
  video?: CMSMedia | null;
  mobileVideo?: CMSMedia | null;
  /** Media Wrapper videoPlayOption: AutoplayLoop | AutoplayOnce | PlayOnClick | OpenPopup */
  videoPlayOption?: string | null;
  /** Fixed media heights in px; null = auto (asset driven) */
  mediaHeightDesktop?: number | null;
  mediaHeightMobile?: number | null;
  /** Typography sub-entry values (flattened) */
  textColor?: string | null;
  textSize?: string | null;
  textAlignment?: string | null;
  /** 'medium' = 50/50 media/text split on side-by-side cards; 'large' = text ~2/3 */
  textMaxWidth?: string | null;
  backgroundColor?: string | null;
  /** Spacing presets — raw Contentful values ('Default' | 'none' | '1'..'4') */
  paddingTopBottom?: string | null;
  paddingLeftRight?: string | null;
  marginBottom?: string | null;
  contentHorizontalAlignment?: string | null;
  contentVerticalAlignment?: string | null;
  /** 'All' | 'Desktop' | 'Mobile' | 'none' */
  deviceDisplay?: string | null;
  /** Device-scoped show more/less: 'All' | 'Desktop' | 'Mobile' | 'none' */
  showMoreOn?: string | null;
  /** Entry animation: 'None' | 'Fade' */
  entryEffectType?: string | null;
  /** Countdown target (ISO datetime) — when set, a countdown is shown */
  countdownTo?: string | null;
}

/**
 * Universal Grid — new-generation layout grid holding Universal Cards.
 */
export interface CMSUniversalGrid extends CMSSection {
  __typename: 'UniversalGrid';
  /** Columns (decimal allowed, e.g. 1.5 shows a peek of the next column) */
  columnDesktop?: number | null;
  columnMobile?: number | null;
  isSlider?: boolean | null;
  isSliderMobile?: boolean | null;
  hasScrollBar?: boolean | null;
  /** Fixed heights in px; null = auto (content driven) */
  gridHeightDesktop?: number | null;
  gridHeightMobile?: number | null;
  /** Spacing presets — raw Contentful values ('Default' | 'none' | '1'..'4') */
  columnsGap?: string | null;
  rowsGap?: string | null;
  /** Container width preset: 'Full' | 'XL' | 'L' | 'M' | 'S' | 'XS' (absent = XL) */
  maxWidth?: string | null;
  paddingTopBottom?: string | null;
  paddingLeftRight?: string | null;
  marginBottom?: string | null;
  contentHorizontalAlignment?: string | null;
  contentVerticalAlignment?: string | null;
  /** 'All' | 'Desktop' | 'Mobile' | 'none' */
  deviceDisplay?: string | null;
  backgroundColor?: string | null;
  backgroundMediaDesktop?: CMSMedia | null;
  /** Mobile background; falls back to desktop when null */
  backgroundMediaMobile?: CMSMedia | null;
  /** Entry animation: 'None' | 'Fade' */
  entryEffectType?: string | null;
  contentCollection?: { items?: CMSUniversalCard[] | null } | null;
}

/**
 * Iframe embed component
 */
export interface CMSIframe extends CMSSection {
  __typename: 'Iframe';
  url?: string | null;
  height?: number | null;
  maxWidth?: string | null;
  /** Whether to allow native scrollbars inside the iframe */
  hasScrollbar?: boolean | null;
}

/**
 * Quick links navigation component
 */
export interface CMSNavQuickLinks extends CMSSection {
  __typename: 'NavQuickLinks';
  type?: string | null;
  isSticky?: boolean | null;
  linksCollection?: { items?: CMSCallToAction[] | null } | null;
  navQuickLinkBackgroundColor?: string | null;
}

// ============================================
// Homepage/Page Sections
// ============================================

/**
 * Hero slider (multiple slides)
 */
export interface CMSHeroSlider extends CMSSection {
  __typename: 'HeroSlider';
  slides: CMSHeroSlide[];
  autoplay?: boolean;
  interval?: number;
  /** Enable the Dynamic Hero layout (thumbnail strip instead of dots) */
  dynamicBlogHero?: boolean;
  /** Sticky positioning at the top of the viewport */
  fixToTop?: boolean;
}

/**
 * Individual hero slide
 */
export interface CMSHeroSlide {
  __typename: 'HeroSlide';
  sys: CMSSys;
  /** Editor-defined HTML anchor ID from Contentful (distinct from sys.id) */
  anchorId?: string | null;
  heading?: string | null;
  subheading?: string | null;
  subtitleAbove?: string | null;
  media?: CMSResponsiveMedia | null;
  video?: CMSVideo | null;
  cta?: CMSCallToAction | null;
  /** Raw CTA variant from Contentful (e.g. 'secondaryBtn', 'linkDefault') */
  ctaVariant?: string | null;
  textColor?: 'light' | 'dark' | null;
  /** Source content type for CTA label logic ('Card' uses cta.text, 'Page' uses i18n "Discover") */
  sourceType?: 'Card' | 'Page' | null;
  /** Page slug for auto-generated CTA links */
  slug?: string | null;
  /**
   * Country codes (lowercase, ISO 3166-1 alpha-2) where this slide is enabled.
   * `null` or empty array means the slide is enabled in every country.
   */
  localizationConfiguration?: string[] | null;
}

/**
 * Image triptych (3 images side-by-side)
 */
export interface CMSImageTriptych extends CMSSection {
  __typename: 'ImageTriptych';
  images: [CMSMedia, CMSMedia, CMSMedia];
}

/**
 * Content card
 * Unified card type that supports all card variants (HeroBanner, IntroText, etc.)
 */
export interface CMSCard extends CMSSection {
  __typename: 'Card';
  /** Card variant: HeroBanner, IntroText, MediaAndSideText, MediaBanner, Text, Story, SectionBanner, etc. */
  type?: string | null;

  // === Core Content ===
  /** Main heading text (extracted from placeholderHeading.text or title.text) */
  heading?: string | null;
  /** Secondary heading/subtitle */
  subHeading?: string | null;
  /**
   * Date line rendered between the surtitle and the title (e.g. a founding
   * year). Only the UG adapter populates this — other brands leave it unset so
   * the component needs no brand check to decide whether to render it.
   */
  paragraphDate?: string | null;
  /** Subtitle displayed above the main heading */
  subtitleAbove?: string | null;
  /** Body content (plain text extracted from rich text) */
  body?: string | null;
  /** Raw rich text for components that need full rendering (cardText) */
  richText?: CMSRichText | null;
  /** Secondary rich text column (cardText2) for TextWithColumns */
  richText2?: CMSRichText | null;

  // === Media ===
  /** Desktop/primary image */
  image?: CMSMedia | null;
  /** Mobile-specific image */
  mobileImage?: CMSMedia | null;
  /** Video media */
  video?: CMSMedia | null;
  /** Mobile-specific video */
  mobileVideo?: CMSMedia | null;
  youtubeUrl?: string | null;
  /**
   * Link target authored on the media entry itself
   * (`ImageWithFocalPoint.url`), which makes the whole image clickable.
   * Distinct from the card's CTAs.
   */
  mediaUrl?: string | null;

  // === CTAs ===
  /** Primary call-to-action */
  cta?: CMSCallToAction | null;
  /** Secondary call-to-action */
  cta2?: CMSCallToAction | null;
  /** CTA button style (buttonDefault, buttonOutlineWhite, linkDefault, etc.) */
  ctaVariant?: string | null;
  /** Secondary CTA button style */
  cta2Variant?: string | null;
  /** CTA flex direction (row or column) */
  urlDirection?: string | null;
  /** CTA position within container (center, flex-start, flex-end) */
  urlPosition?: string | null;

  // === Styling ===
  /** Text color (hex or preset name) */
  textColor?: string | null;
  /** Background color (hex value) */
  backgroundColor?: string | null;
  /** Text alignment (left, center, right) */
  textAlignment?: 'left' | 'center' | 'right' | null;
  /** Content/layout alignment */
  contentAlignment?: string | null;
  /** Content max width preset (small, medium, large) */
  contentMaxWidth?: string | null;
  /** Content option controlling render order (headingAboveText, headingBelowText) */
  contentOption?: string | null;
  /** Vertical content alignment (HeroBanner variant) */
  verticalAlignment?: 'bottom' | 'center' | 'top' | null;
  /** Text size preset */
  textSize?: 'default' | 'compact' | 'large' | null;
  /** Background/media alignment */
  backgroundAlignment?: string | null;
  /** Overlay opacity for media backgrounds (0-1) */
  overlayOpacity?: number | null;

  // === Spacing ===
  /** Top margin (none, small, medium, large) */
  marginTop?: string | null;
  /** Bottom margin (none, small, medium, large) */
  marginBottom?: string | null;
  /** Component padding */
  componentPadding?: string | null;
  /** Component min-height in pixels */
  componentHeight?: number | null;
  /** Stretch the card to fill its container height */
  componentFullHeight?: boolean | null;

  // === Animation ===
  /** Animation effect (zoomOnHover, scrollEnlarge, etc.) */
  mediaAnimationEffect?: string | null;
  /** Animation trigger point (early, center, late) */
  animationTriggerPoint?: string | null;
  /** Animation speed (slow, medium, fast) */
  animationSpeed?: string | null;
  /** Content transition type (none, fade, slideUp) */
  transitionType?: string | null;

  // === Visibility ===
  /** Show on desktop */
  visibleInDesktop?: boolean | null;
  /** Show on mobile */
  visibleInMobile?: boolean | null;
  /** Enable "show more" text expansion */
  showMore?: boolean | null;

  // === Heading Styling (for components that render styled headings) ===
  /** Heading color */
  headingColor?: string | null;
  /** Heading highlight/accent color */
  headingHighlightColor?: string | null;
  /** Heading semantic tag (h1, h2, h3, etc.) */
  headingTag?: string | null;
  /** Heading style variation */
  headingType?: string | null;
  /** Heading typography variation from Contentful Heading sub-entry */
  headingVariation?: string | null;
  /** Heading-specific text alignment (from the Heading sub-entry, independent of card textAlignment) */
  headingTextAlignment?: 'left' | 'center' | 'right' | null;

  // === Icon (for MyAccount cards) ===
  /** Icon image displayed in yellow circle */
  icon?: CMSMedia | null;

  // === Tags ===
  /** Key-value tags from Contentful tagsCollection */
  tags?: CMSTag[] | null;

  // === Countdown (HeroBanner / GenericCountdownCard variants) ===
  /** Whether to render a countdown timer overlay on the card */
  countdown?: boolean | null;
  /** ISO datetime string the countdown ticks down to */
  countdownToDatetime?: string | null;

  // === Product binding ===
  /** Saleor product SKU bound to this card (used by add-to-cart flows) */
  saleorProductSku?: string | null;

  // === Background positioning ===
  /** CSS-style background-position keyword (e.g. 'center', 'top right') */
  backgroundPosition?: string | null;

  // === Video behaviour ===
  /**
   * Autoplay/loop policy from Contentful's `ImageWithFocalPoint.videoPlayOption`.
   * - `AutoplayLoop` → autoplay + loop
   * - `AutoplayOnce` → autoplay, no loop
   * - `PlayOnClick` / `OpenPopup` → no autoplay, no loop
   * Defaults to `AutoplayLoop` behaviour when null/missing.
   */
  videoPlayOption?: string | null;
}

/**
 * Content group (container for other sections)
 */
export interface CMSContentGroup extends CMSSection {
  __typename: 'ContentGroup';
  heading?: string | null;
  layout?: 'grid' | 'stack' | 'carousel' | null;
  /** Original type from CMS (e.g., 'DoubleCard', 'ThreeColumnStack') */
  type?: string | null;
  items: CMSContentItem[];
}

/**
 * Banner section
 *
 * Used for both regular Banner and BannerHero (CollectionGroup banner).
 * BannerHero uses title/text/urlType, while regular Banner uses heading/body.
 */
export interface CMSBanner extends CMSSection {
  __typename: 'Banner';
  heading?: string | null;
  /** Sub-heading shown alongside or below the main heading */
  subHeading?: string | null;
  body?: string | null;
  /** Rich text content (Breitling uses this instead of body) */
  bannerText?: { json?: unknown } | null;
  media?: CMSMedia | null;
  cta?: CMSCallToAction | null;
  textColor?: 'light' | 'dark' | null;
  backgroundColor?: string | null;
  imagePosition?: 'left' | 'right' | null;
  textAlignment?: 'left' | 'center' | 'right' | null;
  /** BannerHero-specific: title field (maps to heading) */
  title?: string | null;
  /** BannerHero-specific: heading-style variant (e.g. 'default', 'capitalized') */
  titleStyle?: string | null;
  /** BannerHero-specific: text/description (may contain HTML) */
  text?: string | null;
  /** BannerHero-specific: image (direct, not responsive) */
  image?: CMSMedia | null;
  /** BannerHero-specific: CTA type ('button' or 'link') */
  ctaType?: string | null;
}

// ============================================
// Navigation & Layout
// ============================================

/**
 * Alert banner (top of page notification)
 */
export interface CMSAlertBanner extends CMSSection {
  __typename: 'AlertBanner';
  text?: string | null;
  link?: CMSCallToAction | null;
  backgroundColor?: string | null;
  textColor?: string | null;
}

/**
 * Navigation menu item
 */
export interface CMSMenuItem {
  sys: CMSSys;
  label: string;
  url?: string | null;
  type?: 'link' | 'collection' | 'category' | null;
  items?: CMSMenuItem[] | null;
  image?: CMSMedia | null;
}

/**
 * Navigation mega menu
 */
export interface CMSMegaMenu extends CMSSection {
  __typename: 'MegaMenu';
  items: CMSMenuItem[];
}

/**
 * Header configuration
 */
export interface CMSHeader extends CMSSection {
  __typename: 'MainHeader';
  logo?: CMSMedia | null;
  alertBanner?: CMSAlertBanner | null;
  megaMenu?: CMSMegaMenu | null;
  quickLinks?: CMSCallToAction[] | null;
}

/**
 * Footer link group
 */
export interface CMSFooterLinkGroup {
  sys?: CMSSys;
  heading?: string | null;
  links: CMSCallToAction[];
}

/**
 * Footer configuration
 */
export interface CMSFooter extends CMSSection {
  __typename: 'Footer' | 'SimpleFooter';
  logo?: CMSMedia | null;
  linkGroups?: CMSFooterLinkGroup[] | null;
  socialLinks?: CMSCallToAction[] | null;
  legalLinks?: CMSCallToAction[] | null;
  copyright?: string | null;
}

/**
 * CMS-authored breadcrumb block (BEC-24794). The entry controls placement and
 * style only — the crumb items are generated by the app, not authored.
 */
export interface CMSBreadcrumb extends CMSSection {
  __typename: 'Breadcrumb';
  /** 'Light' renders the inverted bar for dark contexts (legacy semantic). */
  style?: 'Dark' | 'Light' | null;
}

/**
 * SEO metadata
 */
export interface CMSSeoConfiguration {
  title?: string | null;
  description?: string | null;
  keywords?: string[] | null;
  ogImage?: CMSMedia | null;
  noIndex?: boolean | null;
  noFollow?: boolean | null;
  canonical?: string | null;
}

/**
 * Page configuration
 */
/**
 * Page-level header override from Contentful.
 * When a CMS Page has a `mainHeader` entry, it overrides the layout-level
 * MainHeader for that specific page (e.g. 404 page with a transparent header).
 */
export interface CMSPageHeader {
  background?: string | null;
  textColorOption?: string | null;
  positionBehavior?: string | null;
  backgroundImage?: string | null;
  logoLight?: CMSMedia | null;
  logoDark?: CMSMedia | null;
  logoWidthDesktop?: number | null;
  logoHeightDesktop?: number | null;
  logoWidthMobile?: number | null;
  logoHeightMobile?: number | null;
}

export interface CMSPage {
  sys: CMSSys;
  slug: string;
  /**
   * Contentful `page.type` (`Editorial`, `Magazine`, …). Mapped by the UG adapter
   * only; undefined for the other brands.
   */
  type?: string | null;
  pageTitle?: string | null;
  /**
   * Page excerpt from Contentful `shortDescription`. Feeds the meta-description
   * fallback when no `seoDescription` is authored. Mapped by the UG adapter only.
   */
  shortDescription?: string | null;
  /**
   * Page thumbnail from Contentful. Feeds the `og:image` fallback when the SEO
   * configuration carries no thumbnail. Mapped by the UG adapter only.
   */
  thumbnail?: CMSMedia | null;
  seo?: CMSSeoConfiguration | null;
  content: CMSContentItem[];
  /** Fixed-page bands above transactional content (when adapted separately). */
  topContent?: CMSContentItem[];
  /** Fixed-page bands below transactional content (when adapted separately). */
  bottomContent?: CMSContentItem[];
  /**
   * Countries where this page is published, as set in Contentful.
   * Used to filter hreflang tags so only locales where the page
   * is available receive alternate links (per spec §6).
   *
   * Array of lowercase ISO 3166-1 alpha-2 country codes (e.g. `['us', 'ca', 'gb']`).
   * When null/undefined, hreflang tags are generated for all primary locales.
   */
  localizationConfiguration?: string[] | null;
  /**
   * Raw Contentful content items before adaptation (legacy preview shape).
   */
  rawContentItems?: unknown[];
  /**
   * Original full, encoded GraphQL response required by Contentful live updates.
   * Set in draft mode — other brands omit this field.
   */
  rawPreviewData?: Record<string, unknown>;
  /**
   * Page-level header override from Contentful `mainHeader` field.
   * When present, overrides the layout-level MainHeader for this page.
   */
  mainHeader?: CMSPageHeader | null;
}

/**
 * Detail page configuration (product pages)
 */
export interface CMSDetailPage {
  sys: CMSSys;
  productType: string; // 'watch', 'strap', 'accessory'
  sku?: string | null;
  collection?: string | null;
  model?: string | null;
  content: CMSContentItem[];
}

// ============================================
// Union Types
// ============================================

/**
 * Any CMS content item
 * Union of all possible content sections
 */
export type CMSContentItem =
  // Product page sections
  | CMSPlaceholderProductDetails
  | CMSPlaceholder
  | CMSPlaceholderBoutiquesInfo
  | CMSPlaceholderBoutiquesAbout
  | CMSPlaceholderModelStory
  | CMSTrendingProducts
  | CMSRegisterWatch
  // Page sections
  | CMSHeroSlider
  | CMSHeroSlide
  | CMSImageTriptych
  | CMSCard
  | CMSContentGroup
  | CMSBanner
  | CMSAlertBanner
  // Listing page sections
  | CMSHeroProductListing
  // Shared content components
  | CMSHeading
  | CMSFreeTextContent
  | CMSGrid
  | CMSUniversalGrid
  | CMSUniversalCard
  | CMSIframe
  | CMSNavQuickLinks
  | CMSImage
  | CMSPopup
  // Gallet-specific
  | CMSStoriesBanner
  | CMSStoriesList
  | CMSCategoryBannerGrid
  | CMSDoubleBanner
  | CMSEBoutiqueServices
  | CMSTimeline
  // Breitling-specific
  | CMSCollectionGroup
  | CMSServices
  | CMSFindAStore
  | CMSContactUs
  | CMSFaq
  | CMSFaqCategories
  | CMSPlaceholderStores
  | CMSPlaceholderService
  // Navigation
  | CMSHeader
  | CMSFooter
  | CMSMegaMenu
  | CMSBreadcrumb
  // Generic section (fallback)
  | CMSSection;

/**
 * Type guard: Check if content is a specific type
 */
export function isCMSType<T extends CMSContentItem>(
  content: CMSContentItem | null | undefined,
  typename: T['__typename']
): content is T {
  return content?.__typename === typename;
}

/**
 * Type guard: Check if content is a product detail section
 */
export function isProductSection(
  content: CMSContentItem | null | undefined
): content is CMSPlaceholderProductDetails | CMSPlaceholder | CMSPlaceholderModelStory {
  if (!content) return false;
  return (
    content.__typename === 'PlaceholderProductDetails' ||
    content.__typename === 'Placeholder' ||
    content.__typename === 'PlaceholderModelStory'
  );
}

/**
 * Type guard: Check if content has media
 */
export function hasMedia(
  content: CMSContentItem | null | undefined
): content is CMSBanner | CMSCard {
  if (!content) return false;
  return 'media' in content || 'video' in content;
}

// ============================================
// Legacy Compatibility Aliases
// ============================================
// These aliases allow gradual migration from legacy types.
// New code should use CMS* types directly.

/**
 * @deprecated Use CMSContentItem instead
 */
export type ContentfulFragment = CMSContentItem;

/**
 * @deprecated Use CMSCard instead
 */
export type ContentfulCardFragment = CMSCard;

/**
 * @deprecated Use CMSContentItem['__typename'] instead
 */
export type ContentfulTypename = CMSContentItem['__typename'];

/**
 * @deprecated Use CMSContentItem instead
 */
export type ContentfulData = CMSContentItem;

/**
 * @deprecated Use CMSContentItem[] instead
 */
export interface PageContent {
  items: CMSContentItem[];
}
